import React from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FaCoins, FaOilCan, FaBrain, FaCheckCircle,
  FaBolt, FaArrowRight, FaGem, FaStar, FaFire
} from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { TiltCustomCard } from "./customPlanCard";

const fmt = (n) => Number(n || 0).toLocaleString();

/* ── Theme ── */
const T = {
  bg: "#050d1a",
  bgCard: "#0a1628",
  border: "#1a2d4a",
  accent: "#eab308",
  accentDim: "rgba(234,179,8,0.1)",
  blue: "#3b82f6",
  textPrimary: "#f0f6ff",
  textSecondary: "#94a3b8",
  textMuted: "#4b6280",
  success: "#22c55e",
};

/* ── Entrance directions per card index ── */
// card 0 → from left, card 1 → from right, card 2 (custom) → from top
const entranceVariants = [
  { hidden: { opacity: 0, x: -80, rotateY: -15 }, visible: { opacity: 1, x: 0, rotateY: 0 } },
  { hidden: { opacity: 0, x: 80,  rotateY: 15  }, visible: { opacity: 1, x: 0, rotateY: 0 } },
  { hidden: { opacity: 0, y: -60, scale: 0.9   }, visible: { opacity: 1, y: 0, scale: 1   } },
];

export default function InvestPlanCards() {
  const navigate = useNavigate();
  const [customAmount, setCustomAmount] = React.useState("");

  const plans = [
    {
      id: 1,
      planKey: "plan1",
      icon: FaCoins,
      title: "Core Growth",
      investment: 100,
      monthlyROI: "30%",
      dailyMinPct: 1,
      dailyMaxPct: 1.5,
      bullets: ["Daily profit credit", "Secure withdrawals", "Starter Friendly"],
      accentColor: T.blue,
      gradientFrom: "#1e40af",
      gradientTo: "#3b82f6",
      glowColor: "rgba(59,130,246,0.25)",
    },
    {
      id: 2,
      planKey: "plan2",
      icon: FaOilCan,
      title: "Institutional Blend",
      investment: 500,
      monthlyROI: "40%",
      dailyMinPct: 1.5,
      dailyMaxPct: 2,
      bullets: ["Oil exposure (WTI)", "Protection layer", "Priority support"],
      accentColor: T.accent,
      gradientFrom: "#92400e",
      gradientTo: "#eab308",
      glowColor: "rgba(234,179,8,0.3)",
      isPopular: true,
    },
  ];

  return (
    <section style={{
      padding: "80px 0",
      // background: T.bg,
      position: "relative",
      overflow: "hidden",
    }}>
      {/* section bg glow */}
      <div style={{
        position: "absolute", top: "30%", left: "50%", transform: "translateX(-50%)",
        width: 600, height: 400, borderRadius: "50%",
        background: "radial-gradient(ellipse,rgba(234,179,8,0.05) 0%,transparent 70%)",
        pointerEvents: "none",
      }} />

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>

        {/* section header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{ textAlign: "center", marginBottom: 64 }}
        >
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "6px 18px",
            background: "rgba(234,179,8,0.08)",
            border: "1px solid rgba(234,179,8,0.25)",
            borderRadius: 999,
            color: T.accent, fontSize: 10, fontWeight: 900,
            letterSpacing: "0.25em", textTransform: "uppercase",
            marginBottom: 20,
          }}>
            <FaStar size={10} /> Investment Tiers
          </div>
          <h2 style={{
            fontSize: "clamp(2rem,4vw,3rem)",
            fontWeight: 900, color: T.textPrimary,
            letterSpacing: "-0.04em", textTransform: "uppercase",
            margin: "0 0 12px",
          }}>
            Choose Your <span style={{ color: T.accent }}>Node</span>
          </h2>
          <p style={{ fontSize: 14, color: T.textSecondary, maxWidth: 420, margin: "0 auto" }}>
            Select a secured node path that fits your financial goals.
          </p>
        </motion.div>

        {/* cards grid */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",
          gap: 28,
          alignItems: "start",
        }}>
          {plans.map((plan, index) => (
            <TiltCard
              key={plan.id}
              plan={plan}
              index={index}
              navigate={navigate}
              entranceDir={entranceVariants[index]}
            />
          ))}

          {/* Custom card — enters from bottom-right, last to land */}
          <TiltCustomCard
            customAmount={customAmount}
            setCustomAmount={setCustomAmount}
            navigate={navigate}
          />
        </div>
      </div>
    </section>
  );
}

function TiltCard({ plan, index, navigate, entranceDir }) {
  const Icon = plan.icon;
  const { isLoggedIn } = useAuth();

  /* 3D tilt — logic unchanged */
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-100, 100], [10, -10]);
  const rotateY = useTransform(x, [-100, 100], [-10, 10]);

  function handleMouse(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    x.set(event.clientX - rect.left - rect.width / 2);
    y.set(event.clientY - rect.top - rect.height / 2);
  }

  const goToInvestment = (payload) => {
    if (!isLoggedIn) {
      navigate("/login", { state: { from: "/investment", planPayload: payload } });
      return;
    }
    navigate("/investment", { state: payload });
  };

  return (
    <motion.div
      initial={entranceDir.hidden}
      whileInView={entranceDir.visible}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.15 + index * 0.22 }}
      style={{ perspective: 1200, position: "relative" }}
      onMouseMove={handleMouse}
      onMouseLeave={() => { x.set(0); y.set(0); }}
    >
      
      {/* Popular badge */}
      {plan.isPopular && (
        <motion.div
          initial={{ opacity: 0, y: -12, scale: 0.8 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, type: "spring", stiffness: 300 }}
          style={{
            position: "absolute", top: -16, left: "50%", transform: "translateX(-50%)",
            zIndex: 20,
            background: `linear-gradient(90deg,${plan.gradientFrom},${plan.gradientTo})`,
            color: "#0a0d14",
            fontSize: 9, fontWeight: 900, letterSpacing: "0.25em",
            textTransform: "uppercase",
            padding: "6px 18px", borderRadius: 999,
            boxShadow: `0 4px 20px ${plan.glowColor}`,
            display: "flex", alignItems: "center", gap: 6,
            whiteSpace: "nowrap",
          }}
        >
          <FaFire size={9} /> Most Popular
        </motion.div>
      )}

      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        whileHover={{ scale: 1.02 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
      >
        <div style={{
          background: T.bgCard,
          border: `1px solid ${plan.isPopular ? plan.accentColor + "55" : T.border}`,
          borderRadius: 20,
          padding: "20px 18px",
          position: "relative",
          overflow: "hidden",
          boxShadow: plan.isPopular
            ? `0 0 0 1px ${plan.accentColor}22, 0 24px 60px rgba(0,0,0,0.5), 0 0 40px ${plan.glowColor}`
            : "0 12px 48px rgba(0,0,0,0.4)",
          transition: "box-shadow 0.3s",
        }}>

          {/* top color accent line */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 3,
            background: `linear-gradient(90deg,${plan.gradientFrom},${plan.gradientTo})`,
          }} />

          {/* corner glow */}
          <div style={{
            position: "absolute", top: -30, right: -30,
            width: 120, height: 120, borderRadius: "50%",
            background: `radial-gradient(ellipse,${plan.glowColor} 0%,transparent 70%)`,
            pointerEvents: "none",
          }} />

          {/* shimmer sweep on mount */}
          <motion.div
            initial={{ x: "-120%" }}
            whileInView={{ x: "120%" }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 + index * 0.15, duration: 0.8, ease: "easeInOut" }}
            style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(105deg,transparent 30%,rgba(255,255,255,0.04) 50%,transparent 70%)",
              pointerEvents: "none", zIndex: 1,
            }}
          />

          {/* ── Icon ── */}
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            whileInView={{ scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 + index * 0.12, type: "spring", stiffness: 260, damping: 18 }}
            style={{
              width: 48, height: 48, borderRadius: 14,
              background: `linear-gradient(135deg,${plan.gradientFrom},${plan.gradientTo})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 12px",
              boxShadow: `0 4px 16px ${plan.glowColor}`,
              position: "relative", zIndex: 2,
            }}
          >
            <Icon size={18} color="#fff" />
          </motion.div>

          {/* title */}
          <div style={{ textAlign: "center", marginBottom: 12, position: "relative", zIndex: 2 }}>
            <h3 style={{
              fontSize: "0.95rem", fontWeight: 900,
              color: T.textPrimary, letterSpacing: "-0.02em", margin: "0 0 2px",
              textTransform: "uppercase",
            }}>
              {plan.title}
            </h3>
            <span style={{
              fontSize: 9, fontWeight: 900, color: plan.accentColor,
              letterSpacing: "0.25em", textTransform: "uppercase",
            }}>
              Tier Protocol
            </span>
          </div>

          {/* pricing box */}
          <div style={{
            background: "rgba(0,0,0,0.3)",
            border: `1px solid rgba(255,255,255,0.06)`,
            borderRadius: 12, padding: "11px 10px",
            marginBottom: 12, textAlign: "center",
            position: "relative", zIndex: 2,
          }}>
            <div style={{ fontSize: 8, color: T.textMuted, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 4 }}>
              Minimum Stake
            </div>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "center", gap: 2 }}>
              <span style={{ fontSize: 12, fontWeight: 900, color: T.textSecondary, marginTop: 3 }}>$</span>
              <span style={{ fontSize: 26, fontWeight: 900, color: T.textPrimary, letterSpacing: "-0.04em", lineHeight: 1 }}>
                {fmt(plan.investment)}
              </span>
            </div>
            <div style={{
              marginTop: 8, paddingTop: 8,
              borderTop: "1px solid rgba(255,255,255,0.06)",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <span style={{ fontSize: 9, color: T.textMuted, fontWeight: 700, fontStyle: "italic" }}>
                Monthly ROI
              </span>
              <span style={{ fontSize: 13, fontWeight: 900, color: T.success }}>
                {plan.monthlyROI}
              </span>
            </div>
          </div>

          {/* stats */}
          <div style={{ marginBottom: 14, position: "relative", zIndex: 2 }}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "0 2px 9px",
              borderBottom: `1px solid ${T.border}`,
              marginBottom: 8,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 9, color: T.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                <FaBolt style={{ color: plan.accentColor }} size={9} /> Daily Yield
              </div>
              <span style={{ fontSize: 11, fontWeight: 900, color: T.textPrimary }}>
                {plan.dailyMinPct}% – {plan.dailyMaxPct}%
              </span>
            </div>

            {plan.bullets.map((bullet, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 + index * 0.1 + i * 0.08, duration: 0.4 }}
                style={{
                  display: "flex", alignItems: "center", gap: 7,
                  padding: "4px 2px",
                }}
              >
                <FaCheckCircle size={9} style={{ color: plan.accentColor, flexShrink: 0 }} />
                <span style={{ fontSize: 10, color: T.textSecondary, fontWeight: 600 }}>
                  {bullet}
                </span>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.button
            whileHover={{ scale: 1.03, boxShadow: `0 10px 28px ${plan.glowColor}` }}
            whileTap={{ scale: 0.97 }}
            onClick={() => goToInvestment({ amount: String(plan.investment), name: plan.planKey })}
            style={{
              width: "100%", padding: "11px 0",
              background: `linear-gradient(135deg,${plan.gradientFrom},${plan.gradientTo})`,
              color: "#fff",
              fontWeight: 900, fontSize: 10,
              letterSpacing: "0.18em", textTransform: "uppercase",
              border: "none", borderRadius: 12, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
              boxShadow: `0 4px 18px ${plan.glowColor}`,
              transition: "box-shadow 0.25s",
              position: "relative", zIndex: 2,
            }}
          >
            Initialize Plan <FaArrowRight size={9} />
          </motion.button>

          <div style={{
            marginTop: 10, textAlign: "center", opacity: 0.3,
            fontSize: 8, fontWeight: 900, color: T.textSecondary,
            textTransform: "uppercase", letterSpacing: "0.15em",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
            position: "relative", zIndex: 2,
          }}>
            <FaGem size={8} /> Institutional Grade Security
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}